import { isNotEmpty, useForm } from '@mantine/form'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { Group, NumberInput, Stack, Text } from '@mantine/core'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import { formatDateToUTC, stringToDate } from 'app/utils/util-functions'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { LoanParams } from 'app/models/loan-params'
import { getPiks } from 'app/state/ducks/payment-in-kind/selectors'
import { loadPaymentInKinds } from 'app/state/ducks/payment-in-kind/thunks'
import { createDraftPIK, submitPIK } from './pik-daml-actions'
import { PikParams } from 'app/models/pik-params'
import { CustomLoader } from 'app/views/components/loader/loader'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'
import { listOf } from 'app/state/utils/load-result'

type Props = {
  close: any
  loan: LoanParams
}

export default function ProcessPIKForm({ close, loan }: Props) {
  const piks = useSelector(getPiks)
  const businessDate = useSelector(getBusinessDate)
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  // The form reads the loan's existing PIK from Redux; load it here so the
  // form works wherever it is opened from.
  // The form opens once it has settled: before that the existing PIK is
  // unknown and a Save would create a second one.
  const ready = usePrerequisiteReads(
    () => [
      dispatch(loadPaymentInKinds(loan.id)),
    ],
    () => {
      // a failed read is not "no draft": opening would let a Save create a
      // second one. Say so and close; the menu can be opened again.
      ErrorNotification({
        title: 'Could not open',
        message: "The loan's existing PIK could not be loaded. Please try again.",
      })
      close()
    },
    { onLoaded: results => setOwn(listOf<PikParams>(results[0])) },
  )

  // Derived, not copied into state: the form opens in the same render that
  // publishes the loaded list, so an existing PIK must not show as "Create"
  // for one render while a state copy catches up.
  // THIS form's read of the loan's PIKs, kept apart from the shared slice
  // another form's later answer may replace; the slice is the fallback
  const [own, setOwn] = useState<PikParams[]>([])
  const pik = useMemo<PikParams | undefined>(
    () =>
      own.find(candidate => candidate.loanId.id === loan.id) ??
      piks.find(candidate => candidate.loanId.id === loan.id),
    [own, piks, loan],
  )

  // Calculate initial PIK percentage based on loan's pikInterestPercent
  const calculateInitialPercentage = (): number => {

    if (pik?.percentage) {
      return Number(pik.percentage)
    }

    if (loan.pikInterestPercent != null && loan.pikInterestPercent > 0) {
      const allInRate = loan.allInRateWithPikPremium || loan.allInRate
      if (allInRate && allInRate > 0) {
        const calculated = (loan.pikInterestPercent / allInRate) * 100
        return calculated
      }
    }

    // Default to 100% (for PIK_All loans and legacy contracts without pikInterestPercent)
    return 100
  }

  const form: any = useForm({
    initialValues: {
      id: pik?.id,
      percentage: calculateInitialPercentage(),
      pikDate: pik?.pikDate
        ? stringToDate(pik.pikDate)
        : loan.nextPIKDate
          ? stringToDate(loan.nextPIKDate)
          : businessDate,
    },

    transformValues: values => {
      return {
        id: values.id,
        percentage: values.percentage,
        loanId: { id: loan.id, admin: loan.agencyAdmin },
        pikDate: formatDateToUTC(values.pikDate),
      }
    },

    validate: {
      pikDate: isNotEmpty('PIK Date'),
      percentage: (value) => {
        if (!value) {
          return 'PIK percentage is required';
        }
        if (value < 0 || value > 100) {
          return 'PIK percentage must be between 0 and 100';
        }
        return null;
      },
    },
  })

  useEffect(() => {
    if (!pik) return

    let percentage = 100  // Default to 100% (for PIK_All loans and legacy contracts)
    if (pik?.percentage) {
      percentage = Number(pik.percentage)
    } else if (loan.pikInterestPercent != null && loan.pikInterestPercent > 0) {
      const allInRate = loan.allInRateWithPikPremium || loan.allInRate
      if (allInRate && allInRate > 0) {
        percentage = (loan.pikInterestPercent / allInRate) * 100
      }
    }

    form.setValues({
      id: pik?.id,
      percentage: percentage,
      pikDate: stringToDate(pik.pikDate),
    })
  }, [pik])

  const onSubmit = async () => {
    setIsLoading(true)
    const result = await createDraftPIK(
      form.getTransformedValues(),
      dispatch,
      pik,
    )
    setIsLoading(false)
    if (result) {
      close()
    }
  }

  const saveAndSubmit = async () => {
    setIsLoading(true)
    const response: any = await createDraftPIK(
      form.getTransformedValues(),
      dispatch,
      pik,
    )
    if (!response) {
      setIsLoading(false)
      return
    }
    await submitPIK(response, dispatch)

    close()
    setIsLoading(false)
  }

  // the saved record decides create vs edit: nothing is editable until it is known
  if (!ready) return <CustomLoader />

  return (
    <FormWrapper title={(pik ? 'Edit' : 'Create') + " Payment In Kind"}>
      <div className="content">
        <form onSubmit={form.onSubmit(() => onSubmit())}>
          <div className="create-new">
            <Stack spacing="xl">
              {pik && pik?.status !== 'Draft' ? (
                <Text className="topFormErrorText">
                  PIK has already been submitted
                </Text>
              ) : null}
              <Group noWrap>
                <CustomDatePicker
                  disabled={pik && pik?.status === 'Submitted'}
                  required
                  label={'PIK Date'}
                  date={form.values.pikDate}
                  setDate={(value: any) =>
                    form.setFieldValue('pikDate', value)
                  }
                  holidayCalendars={[]}
                />
                <NumberInput
                  withAsterisk
                  w="100%"
                  precision={5}
                  min={0}
                  max={100}
                  label="PIK Percentage"
                  parser={value =>
                    value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                  }
                  formatter={value =>
                    !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                  }
                  {...form.getInputProps('percentage')}
                />
              </Group>
              <Group noWrap mt={10}>
                <PrimaryButton
                  loading={isLoading}
                  type="submit"
                  disabled={!form.isValid() || pik?.status === 'Submitted'}
                  text="Save"
                  w="100%"
                />
                <PrimaryButton
                  loading={isLoading}
                  onClick={() => saveAndSubmit()}
                  disabled={!form.isValid() || pik?.status === 'Submitted'}
                  text="Create and Submit"
                  w="100%"
                />
              </Group>
            </Stack>
          </div>
        </form>
      </div>
    </FormWrapper>
  )
}
